import {computed} from 'vue';

/**
 * Workaround for https://github.com/intlify/vue-i18n-next/issues/1155
 *
 * In newer versions of vue-i18n, $t and $te are becoming legacy.
 *
 * Instead of using built-in useI18n() in our templates,
 * use this composable that handles undefined/null keys.
 */

export const useTranslation = () => {
    const i18n = useI18n();

    // Null-safe `t` function
    const nullSafeT = (key?: string | null, ...args: any[]): string => {
        // If the key is null, undefined, or empty, return an empty string immediately
        if (key == null || key.trim() === '') {
            return '';
        }

        // @ts-expect-error
        return computed(() => i18n.t(key, ...args)).value;
    };

    // Null-safe `te` function
    const nullSafeTe = (key?: string | null, ...args: any[]): boolean => {
        if (key == null || key.trim() === '') {
            return false; // Return false for invalid keys
        }

        return computed(() => i18n.te(key, ...args)).value;
    };

    return {
        ...i18n,
        t: nullSafeT,
        te: nullSafeTe,
    };
};

/**
 * Translation helper for code that runs WITHOUT an active component instance:
 * Pinia store setups, Nuxt hooks, plugins, timers, vue-query callbacks and
 * lazily-evaluated `computed` getters.
 *
 * {@link useTranslation} cannot be used there. It calls `useI18n()`, which throws
 * `MUST_BE_CALL_SETUP_TOP` whenever `getCurrentInstance()` is null. Production
 * builds of @intlify/message-compiler replace the message with the bare error
 * code, so it surfaces as an unreadable `SyntaxError: 26`.
 *
 * That throw is especially destructive inside a Pinia setup store: Pinia caches
 * the partial store before running the setup function, so a single throw leaves
 * the store permanently stripped of all its state and actions, and every later
 * `useSomeStore()` silently returns that stub.
 *
 * `$i18n` is the app-level composer and has no component-instance requirement.
 * Since all messages are declared globally in `i18n.config.ts`, it resolves the
 * same messages `useI18n()` would.
 */
export const useGlobalTranslation = () => {
    type GlobalComposer = { t: (key: string, ...args: any[]) => string, te: (key: string, ...args: any[]) => boolean }

    // Resolved lazily so that nothing i18n-related runs at store setup time.
    const composer = (): GlobalComposer => useNuxtApp().$i18n as unknown as GlobalComposer

    const nullSafeT = (key?: string | null, ...args: any[]): string => {
        if (key == null || key.trim() === '') {
            return '';
        }

        return composer().t(key, ...args);
    };

    const nullSafeTe = (key?: string | null, ...args: any[]): boolean => {
        if (key == null || key.trim() === '') {
            return false;
        }

        return composer().te(key, ...args);
    };

    return {
        t: nullSafeT,
        te: nullSafeTe,
    };
};
